<!-- src/views/pages/dashboard/admin-dashboard/schedules-index.vue -->
<script setup>
import { computed } from "vue";
import { useEmployeeStore } from "@/stores/employeeStore";

const props = defineProps({
  year: { type: Number, required: true },
});

const employeeStore = useEmployeeStore();

const currentYear = new Date().getFullYear();
const isCurrentYear = computed(() => props.year === currentYear);

/* ---------------------------
 * date helpers
 * --------------------------- */
function parseYmd(key) {
  // key = "YYYY-MM-DD"
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1); // local time, safe
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function fmtLabel(d) {
  // Example: 25 Jan 2025
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function ymd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Try to read employee DOB from common fields.
 * Adjust these keys to match your backend.
 */
function pickDob(emp) {
  return (
    emp.date_of_birth ||
    emp.dob ||
    emp.birth_date ||
    emp.birthdate ||
    emp.personal_dob ||
    null
  );
}

/**
 * Build a "birthday date" in the selected year for an employee.
 * If DOB missing/invalid -> null.
 * Handles Feb 29 by clamping to Feb 28 on non-leap years (safe).
 */
function birthdayInYear(emp, year) {
  const raw = pickDob(emp);
  if (!raw) return null;

  const d = new Date(raw);
  if (isNaN(d)) return null;

  const m = d.getMonth(); // 0-based
  const day = d.getDate(); // 1..31

  // Construct date in selected year
  let bd = new Date(year, m, day);
  if (isNaN(bd)) return null;

  // Feb 29 clamp if invalid in that year
  if (m === 1 && day === 29) {
    // if Feb 29 doesn't exist -> JS auto-rolls to Mar 1; clamp to Feb 28
    if (bd.getMonth() !== 1) bd = new Date(year, 1, 28);
  }

  return startOfDay(bd);
}

function empName(e) {
  const a = e.first_name || e.firstname || "";
  const b = e.last_name || e.lastname || "";
  const n = `${a} ${b}`.trim();
  return n || e.name || e.employee_name || "Employee";
}

function empRole(e) {
  return (
    e.designation?.name || e.designation_name || e.job_title || e.title || "-"
  );
}

function empAvatar(e) {
  return e.avatar_url || e.photo_url || e.profile_photo_url || null;
}

/* ---------------------------
 * Compute upcoming birthdays
 * --------------------------- */
const employees = computed(() => employeeStore.employees || []);

const anchorDate = computed(() => {
  // If viewing current year -> anchor = today
  // Else -> anchor = Jan 1 of selected year (makes sense for past/future)
  return isCurrentYear.value
    ? startOfDay(new Date())
    : startOfDay(new Date(props.year, 0, 1));
});

const upcomingBirthdays = computed(() => {
  const year = props.year;
  const anchor = anchorDate.value;

  const list = employees.value
    .map((e) => {
      const bd = birthdayInYear(e, year);
      if (!bd) return null;

      // For current year: show >= today
      // For non-current year: show >= Jan 1 (anchor)
      if (bd < anchor) return null;

      return {
        id: e.id ?? `${empName(e)}-${ymd(bd)}`,
        name: empName(e),
        role: empRole(e),
        avatar: empAvatar(e),
        date: bd,
        dateKey: ymd(bd),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.date - b.date);

  return list;
});

/**
 * Groups:
 * - Current year: Today, Tomorrow, then the next distinct dates (limit)
 * - Non-current year: first distinct dates from Jan 1 (limit)
 */
const birthdayGroups = computed(() => {
  const items = upcomingBirthdays.value;
  const anchor = anchorDate.value;

  const groups = [];
  const byDate = new Map();

  for (const it of items) {
    if (!byDate.has(it.dateKey)) byDate.set(it.dateKey, []);
    byDate.get(it.dateKey).push(it);
  }

  // Helper to push group by dateKey with label
  const pushGroup = (dateKey, label) => {
    const arr = byDate.get(dateKey) || [];
    if (!arr.length) return;
    groups.push({ key: dateKey, label, items: arr });
  };

  if (isCurrentYear.value) {
    const todayKey = ymd(anchor);
    const tomorrowKey = ymd(addDays(anchor, 1));

    pushGroup(todayKey, "Today");
    pushGroup(tomorrowKey, "Tomorrow");

    // next upcoming distinct dates after tomorrow
    const distinctKeys = Array.from(byDate.keys()).sort((a, b) => {
      return parseYmd(a) - parseYmd(b);
    });

    for (const k of distinctKeys) {
      const d = parseYmd(k);
      pushGroup(k, fmtLabel(d));
      if (groups.length >= 4) break;
    }
  } else {
    // Non-current year: just show first 4 upcoming dates from Jan 1
    const distinctKeys = Array.from(byDate.keys()).sort((a, b) => {
      return parseYmd(a) - parseYmd(b);
    });

    for (const k of distinctKeys) {
      const d = parseYmd(k);
      pushGroup(k, fmtLabel(d));
      if (groups.length >= 4) break;
    }
  }

  return groups;
});

</script>

<template>
  <div class="row">
    <!-- Birthdays -->
    <div class="col-xxl-8 col-xl-8 d-flex">
      <div class="card flex-fill">
        <div
          class="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap"
        >
          <div class="d-flex align-items-center gap-2">
            <h5 class="mb-2">Birthdays</h5>
            <span class="badge bg-light text-dark border mb-2">
              {{ year }}
            </span>
          </div>

          <!-- <router-link
            to="/employee/employee-list"
            class="btn btn-light btn-md mb-2"
          >
            View All
          </router-link> -->
        </div>

        <div class="card-body pb-1">
          <div v-if="!birthdayGroups.length" class="text-muted fs-13 py-3">
            No upcoming birthdays found for {{ year }}.
          </div>

          <div v-for="g in birthdayGroups" :key="g.key">
            <h6 class="mb-2">{{ g.label }}</h6>

            <div
              v-for="emp in g.items"
              :key="emp.id"
              class="bg-light p-2 border border-dashed rounded-top mb-3 dq-birthday-card"
            >
              <div class="d-flex align-items-center justify-content-between dq-birthday-row">
                <div class="d-flex align-items-center">
                  <span class="avatar avatar-md">
                    <img
                      v-if="emp.avatar"
                      :src="emp.avatar"
                      class="rounded-circle"
                      alt="img"
                    />
                    <img
                      v-else
                      src="@/assets/img/users/user-38.jpg"
                      class="rounded-circle"
                      alt="img"
                    />
                  </span>

                  <div class="ms-2 overflow-hidden">
                    <h6 class="fs-medium text-truncate mb-1">
                      {{ emp.name }}
                    </h6>
                    <p class="fs-13 mb-0">{{ emp.role }}</p>
                  </div>
                </div>

                <!-- <a href="javascript:void(0);" class="btn btn-secondary btn-xs">
                  <i class="ti ti-cake me-1"></i>Send
                </a> -->
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /Birthdays -->
  </div>
</template>
